import { Flex, SelectSearch, Text } from '@bb/elements-web';
import { useQuery } from '@tanstack/react-query';
import { files as searchFiles } from 'api/SearchController';
import { Icon } from 'components';
import { useDebounce } from 'hooks';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { mimeTypeToIcon } from 'utils';
import { ASSETS_MAX_SEARCH_RESULTS } from '../../constants';

type AssetSearchProps = {
  onSelect: ({ id, url }: { id: string; url?: string }) => void;
};

const AssetSearch = ({ onSelect }: AssetSearchProps) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 350);

  const { data: foundFiles = [], isFetching } = useQuery({
    queryKey: ['fileSearch', debouncedSearchText],
    queryFn: () => searchFiles({ input: debouncedSearchText }),
    enabled: debouncedSearchText.length > 0,
    staleTime: 5000,
    select: data =>
      data.slice(0, ASSETS_MAX_SEARCH_RESULTS).map(asset => ({
        ...asset,
        key: asset.id,
        label: asset.name,
        value: asset.id,
        icon: mimeTypeToIcon(asset.filetype),
      })),
  });

  const isLoading = isFetching && debouncedSearchText.length > 0;

  const placeholder = useMemo(() => {
    if (searchText.length > 0) {
      return '';
    }
    return (
      <span>
        <Icon type="antdSearchOutlined" width={14} /> Search All Files/Folders
      </span>
    );
  }, [searchText]);

  return (
    <StyledSelectSearch
      dataCy="asset-search-container"
      placeholder={placeholder}
      popupMatchSelectWidth
      options={foundFiles}
      optionRender={option => (
        <Flex
          align="center"
          gap={8}
          data-cy={`asset-search-item-${option.data.id}`}
        >
          <Icon type={option.data.icon} width={14} />
          <Text color="primary" ellipsis>
            {option.label}
          </Text>
        </Flex>
      )}
      loading={isLoading}
      searchValue={searchText}
      onSearch={setSearchText}
      onSelect={(_val, data) => onSelect({ id: data.id, url: data.url })}
      listHeight={1000}
    />
  );
};

export default AssetSearch;

const StyledSelectSearch = styled(SelectSearch)`
  && {
    width: 100%;
  }
`;
